import { z } from "zod";
import { UserType } from "../lib/enums";
import { phoneSchema } from "../lib/phone";
import { birthday } from "../lib/date-utils";

// ----- Input schemas -----

export const ValidateTokenInputSchema = z.object({
  token: z.string().min(1, "Token is required"),
});

export const RequestMagicLinkInputSchema = z.object({
  email: z.string().email("Invalid email address"),
  redirect: z.string().optional(),
  recaptcha_token: z.string().min(1, "reCAPTCHA is required"),
});

export const ValidateMagicLinkInputSchema = z.object({
  token: z.string().min(1, "Token is required"),
});

export const CompleteMagicSignupInputSchema = z.object({
  token: z.string().min(1, "Token is required"),
  birthday,
});

// ----- Output schemas -----

export const AuthUserSchema = z.object({
  username: z.string(),
  first_name: z.string().nullable(),
  user_type: z.nativeEnum(UserType),
  is_member: z.boolean(),
});

export const AuthResultSchema = z.object({
  user: AuthUserSchema,
});

export const RequestMagicLinkResultSchema = z.object({
  message: z.string(),
  user_exists: z.boolean(),
  contact_found: z.boolean().optional(),
  not_found: z.boolean().optional(),
  suspended: z.boolean().optional(),
  pending_review: z.boolean().optional(),
});

export const GenericMessageSchema = z.object({
  message: z.string(),
});

export const ValidateTokenResultSchema = z.object({
  valid: z.boolean(),
  email: z.string().optional(),
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  /** True when this is a self-registration token — user must submit name+birthday before admin approval */
  needs_registration: z.boolean().optional(),
});

export const SubmitSelfRegistrationInputSchema = z.object({
  token: z.string().min(1, "Token is required"),
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  birthday,
  phone: phoneSchema,
  address_line1: z.string().min(1, "Street address is required"),
  address_line2: z.string().optional(),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State is required"),
  postal_code: z.string().min(1, "ZIP code is required"),
  ice_name: z.string().min(1, "Emergency contact name is required"),
  ice_phone: phoneSchema,
});

// ----- Inferred types -----

export type ValidateTokenInput = z.infer<typeof ValidateTokenInputSchema>;
export type RequestMagicLinkInput = z.infer<typeof RequestMagicLinkInputSchema>;
export type ValidateMagicLinkInput = z.infer<typeof ValidateMagicLinkInputSchema>;
export type CompleteMagicSignupInput = z.infer<typeof CompleteMagicSignupInputSchema>;
export type AuthUser = z.infer<typeof AuthUserSchema>;
export type AuthResult = z.infer<typeof AuthResultSchema>;
export type RequestMagicLinkResult = z.infer<typeof RequestMagicLinkResultSchema>;
export type GenericMessage = z.infer<typeof GenericMessageSchema>;
export type ValidateTokenResult = z.infer<typeof ValidateTokenResultSchema>;
export type SubmitSelfRegistrationInput = z.infer<typeof SubmitSelfRegistrationInputSchema>;
